// AGC: Error boundary component to catch unhandled errors in Base App
// AGC: Prevents "Sorry, we had an unhandled error" blue screen
// AGC: Created v5.0.3 to improve error handling

import React, { Component, ErrorInfo, ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: (error: Error, errorInfo: ErrorInfo, retry: () => void) => ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * AGC: Error Boundary Component
 * AGC: Catches React errors and prevents app crashes
 * AGC: Provides user-friendly error UI with retry option
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // AGC: Update state so next render shows fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // AGC: Log error details for debugging
    console.error('🚨 AGC: Error Boundary caught error:', error)
    console.error('🚨 AGC: Error info:', errorInfo)

    // AGC: Update state with error info
    this.setState({
      error,
      errorInfo,
    })
  }

  handleRetry = () => {
    // AGC: Reset error state to retry rendering
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  render() {
    const { hasError, error, errorInfo } = this.state
    const { children, fallback } = this.props

    // AGC: If error occurred, show fallback UI
    if (hasError && error) {
      // AGC: Use custom fallback if provided
      if (fallback) {
        return fallback(error, errorInfo!, this.handleRetry)
      }

      // AGC: Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 p-4">
          <div className="max-w-md w-full glass-effect rounded-lg p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-red-500/20 rounded-full">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-white mb-2">Oops! Something went wrong</h1>
            <p className="text-gray-300 mb-4">
              Don't worry, we've caught the error. You can try refreshing the page or going back to the main menu.
            </p>

            {/* AGC: Show error message in dev mode */}
            {import.meta.env.DEV && error && (
              <div className="mb-4 p-3 bg-red-900/20 rounded-lg text-left">
                <p className="text-xs font-mono text-red-300 break-all">
                  {error.toString()}
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
              >
                Main Menu
              </button>
            </div>

            {/* AGC: Show stack trace in dev mode */}
            {import.meta.env.DEV && errorInfo && (
              <details className="mt-4 text-left">
                <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300">
                  Show Stack Trace (Dev Only)
                </summary>
                <pre className="mt-2 p-2 bg-gray-800 rounded text-xs text-gray-300 overflow-auto max-h-40">
                  {errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      )
    }

    // AGC: No error, render children normally
    return children
  }
}

export default ErrorBoundary

